import { createContext, FC, PropsWithChildren, useCallback, useContext } from 'react';

import { RegisterActionDispatchFn, RegisterState } from './useRegisterState';

type RegisterContextType = {
  registerState: RegisterState;
  dispatch: RegisterActionDispatchFn;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  goToStep: (index: number) => void;
  skippedNewLabelStep: boolean;
};

const RegisterContext = createContext<RegisterContextType | null>(null);

export const useRegisterContext = () => {
  const context = useContext(RegisterContext);
  if (!context) {
    throw new Error('useRegisterContext must be used within a RegisterContext.Provider');
  }
  return context;
};

interface Props {
  registerState: RegisterState;
  dispatch: RegisterActionDispatchFn;
}

export const RegisterProvider: FC<PropsWithChildren<Props>> = ({ children, registerState, dispatch }) => {
  const goToPreviousStep = useCallback(() => {
    dispatch({
      type: 'SET_STEP_INDEX',
      payload: {
        index: registerState.stepIndex - 1
      }
    });
  }, [dispatch, registerState.stepIndex]);

  const goToNextStep = useCallback(() => {
    dispatch({
      type: 'SET_STEP_INDEX',
      payload: {
        index: registerState.stepIndex + 1
      }
    });
  }, [dispatch, registerState.stepIndex]);

  const goToStep = useCallback(
    (index: number) => {
      dispatch({
        type: 'SET_STEP_INDEX',
        payload: {
          index
        }
      });
    },
    [dispatch]
  );

  return (
    <RegisterContext.Provider
      value={{
        registerState,
        dispatch,
        goToPreviousStep,
        goToNextStep,
        goToStep,
        skippedNewLabelStep: registerState.skippedNewLabelStep
      }}
    >
      {children}
    </RegisterContext.Provider>
  );
};
